/* eslint-disable @typescript-eslint/no-use-before-define */
import Vue from 'vue';

type PromiseState = 'pending' | 'fulfilled' | 'rejected' | 'aborted';

interface ObservablePromiseState {
  state: PromiseState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  error: Error | undefined;
  isError: boolean;
}

/**
 * Wraps a promise into a Vue.observable, so its state, value and error
 * are easily accessible.
 */
export default class ObservedPromise<T> {
  constructor(promise?: Promise<T | undefined> | T) {
    Object.defineProperties(this, {
      _data: { enumerable: false, writable: true },
      promise: { enumerable: false, writable: true },
    });
    const isPending = promise != null && isPromiseLike(promise);
    this._data = Vue.observable({
      state: isPending ? 'pending' : 'fulfilled',
      value: isPending ? undefined : promise,
      error: undefined,
      isError: false,
    });
    this.promise = Promise.resolve(promise);
    if (isPending) {
      this.promise
        .then(value => {
          this._data.value = value;
          this._data.state = 'fulfilled';
        })
        .catch(error => {
          if (error?.name === 'AbortError') {
            this._data.state = 'aborted';
          } else {
            this._data.error = error;
            this._data.state = 'rejected';
          }
        });
    }
  }

  promise: Promise<T | undefined>;

  _data: ObservablePromiseState;

  get value(): T | undefined {
    return this._data.value;
  }

  get error(): Error | undefined {
    return this._data.error;
  }

  get state(): PromiseState {
    return this._data.state;
  }

  get isPending(): boolean {
    return this._data.state === 'pending';
  }

  get isFulfilled(): boolean {
    return this._data.state === 'fulfilled';
  }

  get isError(): boolean {
    return this._data.state === 'rejected';
  }

  get isAborted(): boolean {
    return this._data.state === 'aborted';
  }

  get isFinished(): boolean {
    return !this.isPending;
  }

  /**
   * Returns a new promise that will resolve when current one has been fulfilled
   */
  get onFulfilled(): Promise<void> {
    return new Promise(resolve => {
      this.promise.then(
        () => resolve(),
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
      );
    });
  }

  /**
   * Returns a new promise that will resolve when current one has errored
   */
  get onError(): Promise<void> {
    return new Promise(resolve => {
      this.promise.catch(() => resolve());
    });
  }

  /**
   * Returns a new promise that will resolve when current one has been aborted
   */
  get onAborted(): Promise<void> {
    return new Promise(resolve => {
      this.promise.catch(() => {
        this.isAborted && resolve();
      });
    });
  }

  /**
   * Returns a new promise that will resolve when current one has finished
   */
  get onFinished(): Promise<void> {
    return new Promise(resolve => {
      this.promise.then(
        () => resolve(),
        () => resolve(),
      );
    });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPromiseLike = (obj: any) => {
  return (
    obj && typeof obj.then === 'function' && typeof obj.catch === 'function'
  );
};
